import { Link } from "react-router-dom";
import {
  Heart,
  ShieldCheck,
  Scale,
  Eye,
  Brain,
  Clock,
  ArrowRight,
  Sparkles,
  X,
  MapPin,
  CheckCircle2,
} from "lucide-react";

const USPS = [
  {
    icon: ShieldCheck,
    title: "DSGVO-Hosting Deutschland",
    body: "Deine Daten bleiben in Deutschland und verlassen die EU nicht.",
    accent: "from-brand-500 to-brand-400",
  },
  {
    icon: Scale,
    title: "Beide zahlen gleich",
    body: "5 € im Monat für alle. 30 Tage gratis. Kein Gender-Preis.",
    accent: "from-sunset-500 to-sunset-400",
  },
  {
    icon: Eye,
    title: "Nur echte Matches",
    body: "Likes bleiben unsichtbar, bis beide Seiten mögen. Kein Ego-Ranking.",
    accent: "from-brand-500 to-sunset-500",
  },
  {
    icon: Brain,
    title: "Psychologisches Matching",
    body: "15 Werte-Dimensionen entscheiden, wer wirklich zu dir passt.",
    accent: "from-brand-400 to-brand-600",
  },
  {
    icon: Scale,
    title: "Keine Gender-Bevorzugung",
    body: "Algorithmus behandelt Mann, Frau und divers symmetrisch.",
    accent: "from-sunset-400 to-brand-500",
  },
  {
    icon: Clock,
    title: "10 Tage Warteliste",
    body: "Gute Liebe braucht Zeit. Wir sammeln deine Region und starten gemeinsam.",
    accent: "from-brand-600 to-sunset-500",
  },
];

const STEPS = [
  {
    step: "01",
    title: "Registrieren & Werte setzen",
    body: "In zwei Minuten verrätst du uns, was dir im Leben wichtig ist. 15 Dimensionen, kein endloser Fragebogen.",
  },
  {
    step: "02",
    title: "10 Tage Vorfreude",
    body: "Wir sammeln dein Umfeld. Du bekommst Einblicke in dein kommendes Match-Pool, bevor es losgeht.",
  },
  {
    step: "03",
    title: "Perfekt gematcht",
    body: "Am Launch-Tag siehst du Menschen, die wirklich zu dir passen. Nicht die häufigsten Likes. Die richtigen.",
  },
];

export function Landing() {
  return (
    <div className="min-h-screen bg-white text-ink-900 selection:bg-brand-100 selection:text-brand-700">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[720px] overflow-hidden">
        <div className="absolute -top-40 -left-32 h-[520px] w-[520px] rounded-full bg-brand-200/50 blur-3xl" />
        <div className="absolute -top-20 right-[-10%] h-[480px] w-[480px] rounded-full bg-sunset-400/40 blur-3xl" />
        <div className="absolute top-[260px] left-1/3 h-[320px] w-[320px] rounded-full bg-brand-100 blur-3xl" />
      </div>

      <header className="relative z-10">
        <div className="container flex items-center justify-between py-5">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-gradient shadow-glow">
              <Heart className="h-5 w-5 text-white" fill="currentColor" />
            </span>
            <span className="text-xl font-bold tracking-tight">Parble</span>
          </Link>

          <nav className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/login"
              className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-semibold text-ink-700 hover:text-ink-900 transition"
            >
              Anmelden
            </Link>
            <Link
              to="/register"
              className="btn-primary text-sm px-5 py-2.5"
            >
              Kostenlos starten
              <ArrowRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      </header>

      <main className="relative">
        <section className="container pt-10 pb-24 md:pt-20 md:pb-32">
          <div className="grid items-center gap-12 md:grid-cols-12 md:gap-8">
            <div className="animate-fade-in-up md:col-span-7">
              <div className="chip mb-6">
                <Sparkles className="h-3.5 w-3.5" />
                Free Dating App aus Deutschland
              </div>
              <h1 className="text-5xl font-bold leading-[1.02] tracking-tight sm:text-6xl md:text-7xl">
                Verlieb dich
                <span className="block bg-brand-gradient bg-clip-text text-transparent">
                  ohne Algorithmus-
                </span>
                <span className="block">Spielchen.</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-500">
                Parble matcht nach dem, was dir wirklich wichtig ist &mdash;
                Sicherheit, Familie, Humor, Freiheit. Kein Ego-Ranking,
                kein Gender-Preis. Gehostet in Deutschland.
              </p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Link to="/register" className="btn-primary">
                  30 Tage kostenlos testen
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="#so-funktionierts" className="btn-secondary">
                  Wie es funktioniert
                </a>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-ink-500">
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-brand-500" />
                  Keine Kreditkarte
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-brand-500" />
                  Jederzeit kündbar
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-brand-500" />
                  Danach 5 € / Monat
                </span>
              </div>
            </div>

            <div className="relative md:col-span-5">
              <HeroMockup />
            </div>
          </div>
        </section>

        <section className="container pb-24">
          <div className="mb-14 max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-brand-500">
              Warum Parble
            </p>
            <h2 className="mt-3 text-4xl font-bold tracking-tight md:text-5xl">
              Dating, das sich endlich{" "}
              <span className="bg-brand-gradient bg-clip-text text-transparent">
                fair anfühlt.
              </span>
            </h2>
            <p className="mt-4 text-lg text-ink-500">
              Sechs Dinge, die wir anders machen als der Rest.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {USPS.map(({ icon: Icon, title, body, accent }) => (
              <article
                key={title}
                className="group card relative overflow-hidden p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_70px_-20px_rgba(31,31,46,0.2)]"
              >
                <div
                  className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${accent} shadow-glow`}
                >
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="mt-5 text-xl font-bold tracking-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-ink-500">
                  {body}
                </p>
                <div className="pointer-events-none absolute -bottom-8 -right-8 h-32 w-32 rounded-full bg-brand-100 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
              </article>
            ))}
          </div>
        </section>

        <section
          id="so-funktionierts"
          className="container pb-24"
        >
          <div className="mb-14 max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-brand-500">
              So funktioniert&apos;s
            </p>
            <h2 className="mt-3 text-4xl font-bold tracking-tight md:text-5xl">
              Drei Schritte zu echten Matches.
            </h2>
            <p className="mt-4 text-lg text-ink-500">
              Keine endlosen Swipes. Keine Fake-Profile. Keine Algorithmus-Falle.
            </p>
          </div>

          <ol className="grid gap-5 md:grid-cols-3">
            {STEPS.map(({ step, title, body }, idx) => (
              <li
                key={step}
                className="card relative overflow-hidden p-7"
              >
                <div className="flex items-baseline gap-3">
                  <span className="bg-brand-gradient bg-clip-text text-5xl font-bold tracking-tight text-transparent">
                    {step}
                  </span>
                  {idx < STEPS.length - 1 && (
                    <ArrowRight className="hidden h-5 w-5 text-ink-300 md:block" />
                  )}
                </div>
                <h3 className="mt-6 text-xl font-bold tracking-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-ink-500">
                  {body}
                </p>
              </li>
            ))}
          </ol>
        </section>

        <section className="container pb-24">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-brand-gradient p-10 shadow-glow md:p-16">
            <div className="pointer-events-none absolute -top-20 -right-20 h-72 w-72 rounded-full bg-white/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="relative max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-white backdrop-blur-sm">
                <Heart className="h-3.5 w-3.5" fill="currentColor" />
                Bereit für echte Gefühle?
              </div>
              <h2 className="mt-5 text-4xl font-bold leading-tight tracking-tight text-white md:text-5xl">
                Finde deinen Match &mdash; ohne Spielchen.
              </h2>
              <p className="mt-4 text-lg text-white/90">
                30 Tage kostenlos testen. Danach 5 € im Monat.
                Für alle gleich.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-brand-600 shadow-soft transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                >
                  Jetzt kostenlos starten
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-6 py-3 font-semibold text-white transition-all duration-200 hover:bg-white/10 active:scale-[0.98]"
                >
                  Anmelden
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border bg-surface">
        <div className="container py-10">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-brand-gradient">
                <Heart className="h-4 w-4 text-white" fill="currentColor" />
              </span>
              <div>
                <p className="text-sm font-bold text-ink-900">
                  Parble &mdash; Made in Germany
                </p>
                <p className="text-xs text-ink-500">
                  DSGVO-konform, gehostet in Deutschland.
                </p>
              </div>
            </div>
            <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-ink-500">
              <Link to="/legal/agb" className="hover:text-ink-900 transition">
                AGB
              </Link>
              <Link
                to="/legal/datenschutz"
                className="hover:text-ink-900 transition"
              >
                Datenschutz
              </Link>
              <Link
                to="/legal/impressum"
                className="hover:text-ink-900 transition"
              >
                Impressum
              </Link>
            </nav>
          </div>
          <p className="mt-8 text-xs text-ink-300">
            &copy; {new Date().getFullYear()} Parble. Alle Rechte vorbehalten.
          </p>
        </div>
      </footer>
    </div>
  );
}

function HeroMockup() {
  return (
    <div className="relative mx-auto flex max-w-sm justify-center md:max-w-none">
      <div className="pointer-events-none absolute left-1/2 top-8 h-full w-[85%] -translate-x-1/2 rounded-[3rem] bg-brand-gradient opacity-30 blur-3xl" />

      <div className="absolute -left-4 top-14 z-20 hidden rotate-[-8deg] sm:block">
        <div className="card flex items-center gap-2 px-3 py-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-100">
            <Heart className="h-3.5 w-3.5 text-brand-500" fill="currentColor" />
          </span>
          <div className="text-left">
            <p className="text-[11px] font-semibold text-ink-900">
              Neues Match
            </p>
            <p className="text-[10px] text-ink-500">vor 2 Minuten</p>
          </div>
        </div>
      </div>

      <div className="absolute -right-2 top-40 z-20 hidden rotate-[6deg] sm:block">
        <div className="card flex items-center gap-2 px-3 py-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sunset-500/15">
            <MapPin className="h-3.5 w-3.5 text-sunset-600" />
          </span>
          <div className="text-left">
            <p className="text-[11px] font-semibold text-ink-900">
              In deiner Region
            </p>
            <p className="text-[10px] text-ink-500">Berlin &middot; 4 km</p>
          </div>
        </div>
      </div>

      <div className="relative z-10 w-72 rounded-[3rem] border-[10px] border-ink-900 bg-ink-900 shadow-[0_40px_80px_-20px_rgba(31,31,46,0.35)]">
        <div className="absolute left-1/2 top-2 z-20 h-5 w-24 -translate-x-1/2 rounded-full bg-ink-900" />

        <div className="relative overflow-hidden rounded-[2.1rem] bg-white">
          <div className="flex items-center justify-between px-5 pt-6 pb-3">
            <div className="flex items-center gap-1.5">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-xl bg-brand-gradient">
                <Heart className="h-3.5 w-3.5 text-white" fill="currentColor" />
              </span>
              <span className="text-sm font-bold">Parble</span>
            </div>
            <span className="h-2 w-2 rounded-full bg-brand-500" />
          </div>

          <div className="px-4 pb-4">
            <div className="relative aspect-[3/4] overflow-hidden rounded-3xl bg-brand-gradient">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.35),transparent_55%)]" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white/25 backdrop-blur-sm">
                  <Heart
                    className="h-10 w-10 animate-heart-pulse text-white"
                    fill="currentColor"
                  />
                </div>
              </div>

              <div className="absolute left-4 top-4">
                <span className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold text-ink-900">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Online
                </span>
              </div>

              <div className="absolute inset-x-4 bottom-4 text-white">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-xl font-bold leading-tight">
                      Mia, 28
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-white/90">
                      <MapPin className="h-3 w-3" />
                      Berlin &middot; 4 km
                    </p>
                  </div>
                  <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold backdrop-blur-sm">
                    92% Match
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {["Reisen", "Yoga", "Fotografie"].map((t) => (
                    <span
                      key={t}
                      className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-medium backdrop-blur-sm"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-center gap-4 pb-2">
              <button
                type="button"
                aria-label="Nope"
                className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-border bg-white text-ink-500 shadow-soft transition active:scale-95"
              >
                <X className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Like"
                className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-gradient text-white shadow-glow transition active:scale-95"
              >
                <Heart className="h-6 w-6" fill="currentColor" />
              </button>
              <button
                type="button"
                aria-label="Super Like"
                className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-border bg-white text-sunset-500 shadow-soft transition active:scale-95"
              >
                <Sparkles className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
